import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Reusable Pagination Component
 * 
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Callback when page changes
 * @param {number} totalItems - Total number of items
 * @param {number} pageSize - Items per page
 * @param {boolean} isLoading - Loading state
 * @param {string} className - Additional CSS classes
 */
const Pagination = ({ 
  currentPage = 1, 
  totalPages = 1, 
  onPageChange, 
  totalItems = 0,
  pageSize = 20,
  isLoading = false,
  className = ""
}) => {
  // Don't show pagination if only one page
  if (totalPages <= 1) return null;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && !isLoading) {
      onPageChange(newPage);
    }
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5; // Max pages to show
    
    let start = Math.max(1, currentPage - Math.floor(showPages / 2));
    let end = Math.min(totalPages, start + showPages - 1);
    
    // Adjust start if we're near the end
    if (end - start < showPages - 1) {
      start = Math.max(1, end - showPages + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 ${className}`}>
      {/* Items info */}
      <div className="text-sm text-slate-400">
        <span className="hidden sm:inline">عرض </span>
        <span className="font-semibold text-white">{startItem}</span>
        <span> - </span>
        <span className="font-semibold text-white">{endItem}</span>
        <span> من </span>
        <span className="font-semibold text-white">{totalItems}</span>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-1">
        {/* First page */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || isLoading}
          className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>

        {/* Previous page */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Page numbers */}
        <div className="flex items-center gap-1 mx-2">
          {getPageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              variant={pageNum === currentPage ? "default" : "ghost"}
              size="sm"
              onClick={() => handlePageChange(pageNum)}
              disabled={isLoading}
              className={`h-8 w-8 p-0 text-sm font-medium transition-all ${
                pageNum === currentPage
                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                  : 'text-slate-400 hover:text-white hover:bg-white/10'
              }`}
            >
              {pageNum}
            </Button>
          ))}
        </div>

        {/* Next page */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
          className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Last page */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || isLoading}
          className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
      </div>

      {/* Page size selector (optional) */}
      <div className="text-sm text-slate-400">
        <span>صفحة </span>
        <span className="font-semibold text-white">{currentPage}</span>
        <span> من </span>
        <span className="font-semibold text-white">{totalPages}</span>
      </div>
    </div>
  );
};

export default Pagination;
